/* ============================================================================
   <ltt-carousel> — the shared mobile carousel.

   STANDING RULE (Hamdi, 2026-08-05): no section may make the mobile page long.
   Anything that is a grid on desktop becomes a carousel below 750px — one item
   plus a slice of the next, dots underneath, advancing on its own every 3s.

   USAGE
     <ltt-carousel data-interval="3000">
       <div class="ltt-carousel__viewport ltt-rail ltt-rail--bleed"
            role="region" aria-label="…" tabindex="0">
         <div>…item…</div>
         …
       </div>
       <ul class="ltt-carousel__dots"></ul>   <!-- filled in here -->
     </ltt-carousel>

   TWO OPTIONAL MODES, both off unless the section asks for them:

     data-autoplay="always"  keep advancing at EVERY width, not just below
       750px, and come back ~5s after the reader stops interacting instead of
       surrendering for good. The permanent surrender below is right for a rail
       the reader is expected to take over; it is wrong for a rail whose whole
       point is that it moves (the best-seller row).

     data-autoplay="flow"    a slow, unbroken drift instead of a card-a-time
       shuffle, at data-speed px/second. It NEVER lands on a card and never
       pauses to think, so it needs three things the step mode does not:

         · scroll snapping switched off. Mandatory snap yanks the rail back to
           the nearest card between frames — the two cannot coexist.
         · an infinite track. Once the leading card is fully off the edge it is
           MOVED (never cloned) to the end and the scroll offset is corrected by
           exactly its width, so the seam is invisible and the rail never has to
           rewind. Moving keeps every card real: a cloned card would carry a
           duplicate id and an add-to-cart button that adds the wrong line.
         · its own progress reading. `scrollLeft` now cycles instead of running
           0 → end, so progress is counted in CARDS PASSED, which is what "how
           far through the row am I" actually means here.

       Reduced motion turns the drift off entirely; the rail stays scrollable
       by hand at every width.

     data-progress           the section has authored a
       .ltt-carousel__progress track; this file turns it into a real
       role="scrollbar": the thumb is sized by the visible fraction, tracks the
       rail as it moves — by autoplay, by swipe, by keyboard — and can be
       dragged to scrub. Dots are pointless next to it, so a section that opts
       in simply omits the <ul>.

   DESIGN NOTES, each of which is a bug someone would otherwise hit:

   • It drives NATIVE scroll, never `transform`. The rail is already a
     scroll-snap container, so touch dragging, momentum, keyboard scrolling and
     RTL all work before this file loads. Animating transform would mean
     reimplementing every one of those, worse.

   • It only runs below 750px, and it tears down above it. The same markup is a
     plain grid on desktop; autoplaying a grid would be nonsense.

   • Autoplay stops permanently the first time the reader touches, drags,
     scrolls or focuses inside it. Resuming after someone has taken control is
     the single most irritating thing a carousel does — the reader is looking at
     the item they chose and it slides away.

   • It never advances while off-screen, and never while the tab is hidden.
     Otherwise a reader arrives at a carousel that has silently wound to item 5.

   • prefers-reduced-motion no longer stops anything here (Hamdi 2026-08-16):
     the merchant asked, repeatedly, for the rails to keep moving on every
     device, so autoplay, flow and smooth stepping all run regardless of the OS
     toggle. The carousel is still fully usable by hand.

   • RTL needs no special-casing for direction, because the browser reverses the
     inline axis itself. It DOES need care with scrollLeft, whose sign is
     negative-going in RTL on Firefox/Chrome. Movement and position are
     therefore read from getBoundingClientRect(), which is measured in viewport
     pixels and so has no direction convention to get wrong. `offsetLeft` is
     NOT usable here: in RTL the grid lays item 0 at the far right, so its
     offsetLeft is the LARGEST — scrolling to it sent the rail to the start and
     autoplay never advanced in Arabic.
   ========================================================================= */
(function () {
  'use strict';

  if (customElements.get('ltt-carousel')) return;

  var MOBILE = '(max-width: 749px)';
  var REDUCED = '(prefers-reduced-motion: reduce)';
  var IDLE = 5000; // data-autoplay="always": quiet spell after the reader acts

  customElements.define(
    'ltt-carousel',
    class extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('.ltt-carousel__viewport');
        this.dotList = this.querySelector('.ltt-carousel__dots');
        if (!this.viewport) return;

        this.items = Array.prototype.slice.call(this.viewport.children);
        if (this.items.length < 2) return;

        // aria-controls needs a target id. Derive it from the section id so it
        // is stable across re-renders in the theme editor rather than from a
        // counter, which would change every time a section reloads.
        if (!this.viewport.id) {
          var host = this.closest('.shopify-section');
          this.viewport.id = (host && host.id ? host.id : 'ltt-carousel') + '-rail';
        }

        this.index = 0;
        this.userTook = false;
        this.timer = null;
        this.idleTimer = null;
        this.interval = parseInt(this.dataset.interval, 10) || 3000;
        this.flow = this.dataset.autoplay === 'flow';
        this.always = this.dataset.autoplay === 'always' || this.flow;
        this.speed = parseFloat(this.dataset.speed) || 40; // px per second
        this.passed = 0; // cards that have gone round, for the progress reading

        this.mqMobile = window.matchMedia(MOBILE);
        this.mqReduced = window.matchMedia(REDUCED);

        this.buildDots();
        this.buildArrows();
        this.buildProgress();

        this.onScroll = this.debounce(this.syncFromScroll.bind(this), 90);
        this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
        // The thumb has to track the rail frame by frame, not 90ms later, or a
        // swipe drags the products while the bar lags visibly behind them.
        this.onScrollRaw = this.paintProgress.bind(this);
        this.viewport.addEventListener('scroll', this.onScrollRaw, { passive: true });

        // Any of these means the reader has taken over. One-shot and never
        // undone in the default mode; in `always` mode it only buys them a
        // quiet spell (see surrender()), so the listener has to stay bound.
        ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach(
          function (t) {
            this.viewport.addEventListener(t, this.surrender.bind(this), {
              passive: true,
              once: !this.always,
            });
          }.bind(this)
        );
        this.addEventListener('focusin', this.surrender.bind(this));
        // `always` mode never gives up for good, so it needs the matching
        // release: without it the rail would slide out from under a keyboard
        // reader 5s after they tabbed into a card.
        if (this.always) {
          this.addEventListener('focusout', function (e) {
            if (!this.contains(e.relatedTarget)) this.surrender();
          }.bind(this));
        }

        this.addEventListener('mouseenter', this.pause.bind(this));
        this.addEventListener('mouseleave', this.resume.bind(this));

        // A drag and a per-frame scroll would fight each other, so the drift
        // yields for exactly as long as the finger is down.
        if (this.flow) {
          ['pointerdown', 'touchstart'].forEach(
            function (t) {
              this.viewport.addEventListener(t, function () { this.holding = true; }.bind(this), { passive: true });
            }.bind(this)
          );
          ['pointerup', 'pointercancel', 'touchend', 'touchcancel'].forEach(
            function (t) {
              window.addEventListener(t, function () { this.holding = false; }.bind(this), { passive: true });
            }.bind(this)
          );
        }

        this.onVisibility = function () {
          if (document.hidden) this.pause();
          else this.resume();
        }.bind(this);
        document.addEventListener('visibilitychange', this.onVisibility);

        // Only run while actually on screen.
        this.io = new IntersectionObserver(
          function (entries) {
            this.onScreen = entries[0].isIntersecting;
            if (this.onScreen) this.resume();
            else this.pause();
          }.bind(this),
          { threshold: 0.35 }
        );
        this.io.observe(this);

        this.onMediaChange = this.evaluate.bind(this);
        this.mqMobile.addEventListener('change', this.onMediaChange);
        this.onResize = this.debounce(this.paintProgress.bind(this), 120);
        window.addEventListener('resize', this.onResize);
        this.evaluate();
        this.paintProgress();

        // Mark item 0 as current on connect. Without this the first slide never
        // gets data-current until something advances, so a section keyed off it
        // renders its entrance animation from the second slide onwards only.
        this.paintDots();
      }

      disconnectedCallback() {
        this.pause();
        this.stopFlow();
        clearTimeout(this.idleTimer);
        if (this.io) this.io.disconnect();
        if (this.onMediaChange) this.mqMobile.removeEventListener('change', this.onMediaChange);
        if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
        if (this.onResize) window.removeEventListener('resize', this.onResize);
        if (this.viewport) {
          if (this.onScroll) this.viewport.removeEventListener('scroll', this.onScroll);
          if (this.onScrollRaw) this.viewport.removeEventListener('scroll', this.onScrollRaw);
        }
      }

      /* --- direction ------------------------------------------------------- */

      isRtl() {
        return getComputedStyle(this.viewport).direction === 'rtl';
      }

      /* --- dots ------------------------------------------------------------ */

      buildDots() {
        if (!this.dotList) return;
        this.dotList.innerHTML = '';
        this.dots = this.items.map(
          function (item, i) {
            var li = document.createElement('li');
            var b = document.createElement('button');
            b.type = 'button';
            b.className = 'ltt-carousel__dot';
            b.setAttribute('aria-current', String(i === 0));
            // The label names the destination rather than saying "slide 3":
            // a screen-reader user gets the same information the sighted reader
            // gets from the photograph.
            var name = (item.textContent || '').trim().split('\n')[0].slice(0, 40);
            b.setAttribute('aria-label', name || 'Item ' + (i + 1));
            b.addEventListener(
              'click',
              function () {
                this.surrender();
                this.go(i);
              }.bind(this)
            );
            li.appendChild(b);
            this.dotList.appendChild(li);
            return b;
          }.bind(this)
        );
      }

      /* --- arrows ---------------------------------------------------------- */

      /* Built here rather than authored in the section for the same reason the
         dots are: if this script does not run, an arrow would be a control that
         controls nothing. The rail is still swipeable without either.

         They wrap rather than disabling at the ends, matching autoplay — and the
         dots already say where you are, so a disabled state would be telling the
         reader something they can see. */
      buildArrows() {
        if (!this.dotList) return;

        var ar = (document.documentElement.lang || '').slice(0, 2) === 'ar';
        var labels = {
          prev: this.dataset.prevLabel || (ar ? 'السابق' : 'Previous'),
          next: this.dataset.nextLabel || (ar ? 'التالي' : 'Next'),
        };

        var wrap = document.createElement('div');
        wrap.className = 'ltt-carousel__controls';
        this.dotList.parentNode.insertBefore(wrap, this.dotList);

        this.prevBtn = this.makeArrow('prev', labels.prev, -1);
        wrap.appendChild(this.prevBtn);
        wrap.appendChild(this.dotList);
        this.nextBtn = this.makeArrow('next', labels.next, 1);
        wrap.appendChild(this.nextBtn);
      }

      makeArrow(kind, label, step) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'ltt-carousel__arrow ltt-carousel__arrow--' + kind;
        b.setAttribute('aria-label', label);
        // aria-controls points at the rail, which carries role="region" and its
        // own label, so the relationship is announced rather than implied.
        if (this.viewport.id) b.setAttribute('aria-controls', this.viewport.id);
        b.innerHTML =
          '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">' +
          '<path d="M6 3l5 5-5 5" stroke="currentColor" stroke-width="1.6" ' +
          'stroke-linecap="round" stroke-linejoin="round"/></svg>';
        b.addEventListener(
          'click',
          function () {
            this.surrender();
            this.go(this.index + step);
          }.bind(this)
        );
        return b;
      }

      /* --- progress scrollbar ---------------------------------------------- */

      /* The track is authored by the section (so a section that does not want
         one costs nothing) and upgraded here. role="scrollbar" is the honest
         role: it reports where you are AND moves the thing it reports on, and
         it needs aria-controls + aria-valuenow + a tab stop to be that. */
      buildProgress() {
        this.track = this.querySelector('.ltt-carousel__progress');
        if (!this.track) return;
        this.thumb = this.track.querySelector('.ltt-carousel__progress-thumb');
        if (!this.thumb) return;

        var ar = (document.documentElement.lang || '').slice(0, 2) === 'ar';
        this.track.setAttribute('role', 'scrollbar');
        this.track.setAttribute('aria-orientation', 'horizontal');
        this.track.setAttribute('aria-valuemin', '0');
        this.track.setAttribute('aria-valuemax', '100');
        this.track.setAttribute('tabindex', '0');
        if (this.viewport.id) this.track.setAttribute('aria-controls', this.viewport.id);
        if (!this.track.getAttribute('aria-label')) {
          this.track.setAttribute('aria-label', ar ? 'تمرير المنتجات' : 'Scroll products');
        }

        // Pointer scrubbing. setPointerCapture keeps the drag alive when the
        // finger leaves the 4px-tall track, which on a phone it immediately does.
        var drag = function (e) {
          this.surrender();
          this.scrubTo(e.clientX);
        }.bind(this);

        this.track.addEventListener('pointerdown', function (e) {
          e.preventDefault();
          this.track.setPointerCapture(e.pointerId);
          this.dragging = true;
          drag(e);
        }.bind(this));

        this.track.addEventListener('pointermove', function (e) {
          if (this.dragging) drag(e);
        }.bind(this));

        ['pointerup', 'pointercancel'].forEach(
          function (t) {
            this.track.addEventListener(t, function () {
              this.dragging = false;
            }.bind(this));
          }.bind(this)
        );

        // Arrow keys move by one item, which is what a scrollbar's arrow keys
        // do — and matches what the dots and arrows do elsewhere.
        this.track.addEventListener('keydown', function (e) {
          var back = e.key === 'ArrowLeft' || e.key === 'ArrowUp';
          var fwd = e.key === 'ArrowRight' || e.key === 'ArrowDown';
          if (!back && !fwd) return;
          e.preventDefault();
          this.surrender();
          // Left/right are physical keys: in RTL, "left" moves forward.
          var step = fwd ? 1 : -1;
          if (this.isRtl() && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) step = -step;
          this.go(this.index + step);
        }.bind(this));
      }

      /* Progress along the READING direction, 0 at the first item and 1 at the
         last, whatever sign the engine gives scrollLeft in RTL.

         In flow mode the rail loops, so distance-scrolled says nothing: the
         reading is (cards that have passed + how far the current one has) over
         the card count, which cycles 0 → 1 once per lap through the row. */
      progress() {
        if (this.flow) {
          var n = this.items.length;
          var head = this.viewport.firstElementChild;
          var frac = 0;
          if (head) {
            var rail = this.viewport.getBoundingClientRect();
            var box = head.getBoundingClientRect();
            var hidden = this.isRtl() ? box.right - rail.right : rail.left - box.left;
            if (box.width > 0) frac = Math.min(1, Math.max(0, hidden / box.width));
          }
          var track = this.trackLength();
          return {
            p: ((this.passed + frac) % n) / n,
            visible: track > 0 ? Math.min(1, this.viewport.clientWidth / track) : 1,
            max: 1,
          };
        }
        var max = this.viewport.scrollWidth - this.viewport.clientWidth;
        if (max <= 1) return { p: 0, visible: 1, max: 0 };
        return {
          p: Math.min(1, Math.abs(this.viewport.scrollLeft) / max),
          visible: Math.min(1, this.viewport.clientWidth / this.viewport.scrollWidth),
          max: max,
        };
      }

      /* --- flow mode ------------------------------------------------------- */

      trackLength() {
        return this.viewport.scrollWidth;
      }

      startFlow() {
        if (this.rafId) return;
        // Mandatory snap and a per-frame scroll are mutually exclusive.
        this.classList.add('ltt-carousel--flow');
        var last = null;
        var tick = function (now) {
          this.rafId = requestAnimationFrame(tick);
          // A hidden tab panel measures zero, and every rect test would then
          // read "this card is off the edge" — the rail would churn its whole
          // DOM every frame. No box, no drift.
          if (
            this.holding ||
            this.onScreen === false ||
            document.hidden ||
            !this.viewport.clientWidth
          ) {
            last = now;
            return;
          }
          if (last === null) last = now;
          // Clamp the delta: a backgrounded tab hands back one enormous frame,
          // which would teleport the rail on return.
          var dt = Math.min(64, now - last);
          last = now;
          this.drift((this.speed * dt) / 1000);
        }.bind(this);
        this.rafId = requestAnimationFrame(tick);
      }

      stopFlow() {
        if (!this.rafId) return;
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }

      drift(px) {
        var sign = this.isRtl() ? -1 : 1;
        this.viewport.scrollBy({ left: sign * px, behavior: 'auto' });

        // Recycle every card that has fully left the leading edge — plural,
        // because one frame after a resize can clear more than one.
        for (var guard = 0; guard < this.items.length; guard++) {
          var head = this.viewport.firstElementChild;
          if (!head) break;
          var rail = this.viewport.getBoundingClientRect();
          var box = head.getBoundingClientRect();
          var gone = this.isRtl() ? box.left >= rail.right : box.right <= rail.left;
          if (!gone) break;
          // The correction is MEASURED, not computed from width + gap: those
          // are fractional (250.25 + 24 on this rail) and the engine rounds the
          // scroll offset, so arithmetic slipped a quarter pixel every lap.
          // Watching the card that becomes the new head and undoing exactly the
          // shift it took is self-correcting — and `scrollBy`'s `left` is a
          // physical delta, so the same line is right in RTL.
          var keep = head.nextElementSibling;
          var beforeX = keep ? keep.getBoundingClientRect().left : 0;
          this.viewport.appendChild(head);
          if (keep) {
            this.viewport.scrollBy({
              left: keep.getBoundingClientRect().left - beforeX,
              behavior: 'auto',
            });
          }
          this.passed += 1;
        }
        this.paintProgress();
      }

      paintProgress() {
        if (!this.thumb) return;
        var s = this.progress();
        // Nothing to scroll = nothing to scrub. Hidden rather than full-width:
        // a track that cannot move is a control that lies.
        this.track.hidden = s.max === 0;
        this.thumb.style.setProperty('--ltt-cp-size', s.visible);
        this.thumb.style.setProperty('--ltt-cp-pos', s.p);
        this.track.setAttribute('aria-valuenow', Math.round(s.p * 100));
      }

      /* clientX -> scroll position. The thumb's own width is dead travel, so
         the usable track is (track - thumb) and the pointer addresses the
         thumb's CENTRE. In RTL the track runs the other way, so the distance
         is measured from its right edge. */
      scrubTo(clientX) {
        var s = this.progress();
        if (!s.max) return;
        var rect = this.track.getBoundingClientRect();
        var thumbW = this.thumb.getBoundingClientRect().width;
        var usable = rect.width - thumbW;
        if (usable <= 0) return;
        var x = this.isRtl() ? rect.right - clientX : clientX - rect.left;
        var p = Math.min(1, Math.max(0, (x - thumbW / 2) / usable));
        this.viewport.scrollTo({
          left: (this.isRtl() ? -1 : 1) * p * s.max,
          behavior: 'auto',
        });
      }

      paintDots() {
        if (this.dots)
          this.dots.forEach(
            function (d, i) {
              d.setAttribute('aria-current', String(i === this.index));
            }.bind(this)
          );

        // Mark the current item and announce the change. Sections use this to
        // run entrance animations: removing and re-adding the attribute restarts
        // a CSS animation, which is the whole reason it is an attribute toggle
        // rather than a one-time class.
        this.items.forEach(
          function (item, i) {
            if (i === this.index) item.setAttribute('data-current', '');
            else item.removeAttribute('data-current');
          }.bind(this)
        );

        this.dispatchEvent(
          new CustomEvent('ltt-carousel:change', { detail: { index: this.index }, bubbles: true })
        );
      }

      /* --- movement -------------------------------------------------------- */

      go(i) {
        this.index = (i + this.items.length) % this.items.length;
        var item = this.items[this.index];
        // scrollIntoView would also scroll the PAGE to bring the rail into
        // view. A relative scrollBy of the gap measured between two live
        // rectangles keeps the motion inside the rail and is direction-blind:
        // "align this item's leading edge with the rail's leading edge" is the
        // same sentence in both, and no scrollLeft sign is ever assumed.
        var rail = this.viewport.getBoundingClientRect();
        var box = item.getBoundingClientRect();
        var delta = this.isRtl() ? box.right - rail.right : box.left - rail.left;
        // Always smooth — reduced motion no longer downgrades the glide to a
        // jump (Hamdi 2026-08-16: the rails must move, and move smoothly, on
        // every device).
        this.viewport.scrollBy({
          left: delta,
          behavior: 'smooth',
        });
        this.paintDots();
      }

      /* "Current" is the item on the LEADING edge, not the one in the middle.
         go() aligns leading edges, so anything else disagrees with it: a
         desktop rail showing four cards has its centre two cards past its
         leading card, so a centre-based reading pushed the index forward by
         two and every autoplay step then skipped three. */
      syncFromScroll() {
        if (this.flow) return;
        var rail = this.viewport.getBoundingClientRect();
        var rtl = this.isRtl();
        var edge = rtl ? rail.right : rail.left;
        var best = 0;
        var bestDist = Infinity;
        this.items.forEach(function (item, i) {
          var box = item.getBoundingClientRect();
          var d = Math.abs((rtl ? box.right : box.left) - edge);
          if (d < bestDist) {
            bestDist = d;
            best = i;
          }
        });
        if (best !== this.index) {
          this.index = best;
          this.paintDots();
        }
      }

      /* --- autoplay -------------------------------------------------------- */

      evaluate() {
        if (this.flow) {
          this.resume();
          this.paintProgress();
          return;
        }
        if (this.always || this.mqMobile.matches) this.resume();
        else {
          this.pause();
          this.index = 0;
          this.paintDots();
        }
        this.paintProgress();
      }

      resume() {
        // The rails move on every device now (Hamdi 2026-08-16, said more than
        // once). Reduced motion used to switch flow mode off entirely; it no
        // longer does — the continuous drift is exactly the brand feature he
        // kept reporting as missing on the best-seller row. Step mode always
        // ran; flow now runs too.
        // Flow mode is one rAF loop for the element's whole life; the
        // off-screen and hidden-tab tests live inside the tick instead, so a
        // rail that scrolls back into view picks up exactly where it was.
        if (this.flow) {
          this.startFlow();
          return;
        }
        if (this.timer) return;
        if (this.userTook) return;
        if (!this.always && !this.mqMobile.matches) return;
        if (this.onScreen === false || document.hidden) return;
        if (this.always && this.contains(document.activeElement)) return;
        this.timer = setInterval(this.advance.bind(this), this.interval);
      }

      /* One item forward, and back to the start once the LAST item is fully in
         view. Without the end test, `go(index + 1)` on a desktop rail showing
         four of nine cards asks for an item that cannot reach the leading edge:
         the scroll clamps, syncFromScroll snaps the index back, and the rail
         sits at the end forever. */
      advance() {
        if (this.progress().p > 0.995) this.go(0);
        else this.go(this.index + 1);
      }

      pause() {
        if (this.timer) {
          clearInterval(this.timer);
          this.timer = null;
        }
      }

      /* Default: the reader is now in charge, permanently (see the note at the
         top of the file). In `always` mode the section has said the motion IS
         the feature, so the handover is a truce — it holds for IDLE ms after
         the last thing the reader did, which is long enough to read the card
         they stopped on and short enough that the rail is not dead. */
      surrender() {
        // "Never stops" is the whole brief for flow mode: a drag is held only
        // while the finger is down (see the pointer handlers), never longer.
        if (this.flow) return;
        if (!this.always) {
          this.userTook = true;
          this.pause();
          return;
        }
        this.pause();
        clearTimeout(this.idleTimer);
        this.idleTimer = setTimeout(this.resume.bind(this), IDLE);
      }

      debounce(fn, ms) {
        var t;
        return function () {
          clearTimeout(t);
          t = setTimeout(fn, ms);
        };
      }
    }
  );
})();
